import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Drawer } from "@mui/material";
import { FC } from "react";
import { DashboardContainerBody, DashboardContainerHeader, ReverseRow } from "../MoaDashboard.styles";
import Table from "../../../components/Table/Table";
import { DashboardFilter } from "../../../services/dashboard/dashboard.types";
import { ITooltipParams, ValueFormatterParams } from "ag-grid-community";
import { CSVLink } from "react-csv";
import Button from "../../../components/Button/Button";
import { Variant } from "../../../components/Button/Button.types";
import dayjs from "dayjs";
import { useGetTranslationsTest } from "../../../services/dashboard/dashboard.service";

interface TranslationStatusType {
  title: string;
  query: string;
}

export const TranslationStatusTypes = {
  Menus: {
    title: "Menu",
    query: "menus",
  },
  Promotions: {
    title: "Promotion",
    query: "promotions",
  },
  Products: {
    title: "Product",
    query: "products",
  },
  Bundles: {
    title: "Bundle",
    query: "bundles",
  }
}

interface EventTranslationStatusDrawerProps {
  type: TranslationStatusType;
  failed?: boolean;
  filter: DashboardFilter;
  isOpen: boolean;
  close: () => void;
  openDetailsDrawer?: () => void;
}

const EventTranslationStatusDrawer: FC<EventTranslationStatusDrawerProps> = (
    {
      type,
      failed,
      filter,
      isOpen,
      close,
      openDetailsDrawer
    }
) => {
  const muiTheme = createTheme();

  const date = filter.from ?? dayjs();

  const { data: getTranslationsResponse } = useGetTranslationsTest(filter, type.query, failed, isOpen);
  const translationStatuses = getTranslationsResponse?.data ?? [];

  const eventTranslationsColumnDefs = [
    {
      minWidth: 80,
      field: 'eventTime',
      headerName: 'Time',
      sortable: true,
      floatingFilter: false,
      valueFormatter: (params: ValueFormatterParams) => (params.value && createDateAsUTC(new Date(params.value))
          .toLocaleTimeString()),
      tooltipValueGetter: (params: ITooltipParams) => (params.value && createDateAsUTC(new Date(params.value))
          .toLocaleDateString() + ' ' + createDateAsUTC(new Date(params.value)).toLocaleTimeString())
    },
    {
      minWidth: 80,
      field: 'eventType',
      headerName: 'Event',
      sortable: true,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value
    },
    {
      minWidth: 80,
      field: 'objectName',
      headerName: 'Name',
      sortable: true,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value
    },
    {
      minWidth: 80,
      field: 'status',
      headerName: 'Status',
      sortable: true,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value
    },
    {
      minWidth: 300,
      field: 'errorReason',
      headerName: 'Error Reason',
      sortable: true,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value
    }
  ];

  const createDateAsUTC = (date: Date) => {
    return new Date(Date.UTC(
        date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(),
        date.getSeconds()
    ));
  }

  const closeAndOpenDetailsDrawer = () => {
    close()
    openDetailsDrawer && openDetailsDrawer()
  }

  return (
      <ThemeProvider theme={muiTheme} key={'drawer-mui-theme'}>
        <Drawer
            PaperProps={{
              sx: { width: '45%' }
            }}
            anchor={'right'}
            open={isOpen}
            onClose={close}
        >
          <DashboardContainerHeader>{failed && 'Failed '}{type.title} Translations</DashboardContainerHeader>
          <DashboardContainerBody style={{ gap: '1rem' }}>
            <Table
                rowData={translationStatuses}
                returnedRecords={translationStatuses.length}
                columnDefs={eventTranslationsColumnDefs}
                tooltipInteraction={true}
                tooltipShowDelay={0}
                tooltipHideDelay={20000}
            />
            <ReverseRow>
              <CSVLink
                  data={translationStatuses}
                  headers={[
                    { label: 'Time', key: 'eventTime' },
                    { label: 'Type', key: 'eventType' },
                    { label: 'Name', key: 'objectName' },
                    { label: 'Status', key: 'status' },
                    { label: 'Error Reason', key: 'errorReason' }
                  ]}
                  filename={`${type.title}_Translations_${date.format('YYYY-MM-DD')}.csv`}
                  target={'_blank'}
              >
                <Button label={'Export to CSV'} variant={Variant.Secondary}/>
              </CSVLink>
              {openDetailsDrawer &&
                <Button
                  label={'See all'}
                  variant={Variant.Secondary}
                  onClick={closeAndOpenDetailsDrawer}
                />
              }
            </ReverseRow>
          </DashboardContainerBody>
        </Drawer>
      </ThemeProvider>
  )
}

export default EventTranslationStatusDrawer;